import { LegalDoc, LegalDocSet } from '../legal-doc.model';

/**
 * Product-specific terms for ScreenDeck — an ANNEX to the group-wide AGB, not a
 * second contract. § 22 of the base terms ("Gesamter Vertrag") already treats
 * product annexes as part of the agreement, so everything cross-cutting
 * (remuneration, liability, term, governing law) stays in the base text and is
 * deliberately not repeated here: two parallel full contracts for the same
 * customer would conflict.
 *
 * Replaces the former ScreenDeck SPA terms (features/legal/agb). Revision
 * 04.08.2026 — Regulation (EU) 2024/1689 has been generally applicable since
 * 02.08.2026, so § 7 of the base terms and A 4 below state duties in force,
 * not upcoming ones.
 */

const DE: LegalDoc = {
    title: 'Produktbedingungen ScreenDeck',
    meta: 'Anhang zu den Allgemeinen Geschäftsbedingungen · Stand: 4. August 2026',
    tocHeading: 'Inhalt',
    footer: 'Massgeblich ist die deutsche Fassung. Übersetzungen dienen der Verständlichkeit.',
    sections: [
        {
            num: 'A 1',
            title: 'Gegenstand und Rangfolge',
            body: `
        <p>Diese Produktbedingungen ergänzen die Allgemeinen Geschäftsbedingungen der Gastro Management
        Group Oberbillig (nachfolgend «AGB») für das Produkt <strong>ScreenDeck</strong>. Sie regeln
        ausschliesslich das, was für ScreenDeck besonders gilt.</p>
        <p><strong>Rangfolge:</strong> Im Übrigen gelten die AGB unverändert; insbesondere Vergütung
        (§ 8 AGB), Laufzeit und Kündigung (§ 9 AGB), Verfügbarkeit (§ 10 AGB), Gewährleistung (§ 14 AGB),
        Haftung (§ 15 AGB) sowie anwendbares Recht und Gerichtsstand (§ 23 AGB). Widersprechen sich diese
        Produktbedingungen und die AGB, gehen diese Produktbedingungen für ScreenDeck vor. Eine
        individuelle schriftliche Vereinbarung geht beiden vor.</p>
        <p>Diese Produktbedingungen richten sich ausschliesslich an Unternehmen und gewerblich handelnde
        Organisationen. Ein Vertrag mit Verbraucherinnen und Verbrauchern kommt nicht zustande.</p>
      `,
        },
        {
            num: 'A 2',
            title: 'Leistungsumfang ScreenDeck',
            body: `
        <p>ScreenDeck ist eine webbasierte Digital-Signage-Plattform. Der Leistungsumfang umfasst,
        abhängig von den für die Kundin freigeschalteten Funktionen:</p>
        <ul>
          <li>Verwaltung von Bildschirmen, Wiedergabelisten und Inhaltsfolien einschliesslich
              Änderungsnachweis</li>
          <li>Mandantenfähige Benutzer- und Rollenverwaltung</li>
          <li>Echtzeitsteuerung angebundener Bildschirme</li>
          <li>Zeitplanbasierte Wiedergabe nach Wochentag und Zeitfenster</li>
          <li>Automatische Vorschaubilder der Inhaltsfolien</li>
          <li>Auswertung von QR-Code-Scans (A 6)</li>
          <li>Wartungsbanner und automatische Aktualisierung des Abspielclients</li>
          <li>Integrierter Support-Chat mit E-Mail-Benachrichtigung</li>
          <li>Optionaler KI-Assistent zur Inhaltserstellung (A 4)</li>
        </ul>
        <p>Der massgebliche Funktionsstand ergibt sich aus der jeweils gültigen Produktbeschreibung.
        § 2 AGB zur Weiterentwicklung und zur Ankündigung wesentlicher Einschränkungen gilt entsprechend.</p>
      `,
        },
        {
            num: 'A 3',
            title: 'Verantwortung für Anzeigeinhalte',
            body: `
        <p>Die Kundin ist für sämtliche Inhalte, die sie über ScreenDeck auf ihren Bildschirmen
        veröffentlicht, allein verantwortlich. Sie sichert zu, dass diese Inhalte:</p>
        <ul>
          <li>keine Urheber-, Marken- oder sonstigen Schutzrechte Dritter verletzen,</li>
          <li>nicht gegen geltendes Recht verstossen, insbesondere nicht gegen das UWG, das Strafgesetzbuch
              und das Datenschutzgesetz,</li>
          <li>keine irreführenden, ehrverletzenden, diskriminierenden, pornografischen oder sonst
              anstössigen Darstellungen enthalten,</li>
          <li>bei Preis-, Allergen- und Herkunftsangaben den lebensmittelrechtlichen Vorgaben entsprechen;
              verbindlich bleiben stets die Angaben vor Ort.</li>
        </ul>
        <p>Werden Personendaten angezeigt — etwa Namen oder Fotos von Mitarbeitenden —, stellt die Kundin
        sicher, dass sie dazu berechtigt ist und die betroffenen Personen informiert hat. Die Freistellung
        nach § 16 AGB gilt für solche Inhalte uneingeschränkt.</p>
        <p>Die Kundin benennt für jeden Mandanten mindestens eine administrierende Person. Diese
        verantwortet die Verwaltung der Unterkonten und deaktiviert Konten ausscheidender Mitarbeitender
        unverzüglich.</p>
      `,
        },
        {
            num: 'A 4',
            title: 'KI-Assistent',
            body: `
        <p>ScreenDeck stellt optional einen KI-Assistenten zur Inhaltserstellung bereit. Er beruht auf dem
        <strong>Azure OpenAI Service</strong> von Microsoft und wird in europäischen Rechenzentren
        betrieben. § 7 AGB (KI-Funktionen) gilt vollumfänglich; ergänzend gilt:</p>
        <p><strong>Kontingente und Grenzwerte.</strong> Die Nutzung ist durch monatliche Kontingente je
        Mandant und je Nutzerin oder Nutzer begrenzt. Administrierende Personen können mandantenspezifische
        Grenzwerte festlegen; andernfalls gelten die Standardwerte der Anbieterin. Ist das Kontingent
        ausgeschöpft, werden weitere Anfragen mit einem Hinweis abgewiesen. Eine Rückvergütung nicht
        genutzter oder bereits verbrauchter Kontingente erfolgt nicht. Die Anbieterin kann Kontingente,
        Grenzwerte und das eingesetzte Modell anpassen; wesentliche dauerhafte Einschränkungen kündigt sie
        mindestens 14 Tage im Voraus an.</p>
        <p><strong>Prüfpflicht der Kundin.</strong> KI-Ergebnisse sind unverbindliche Vorschläge und können
        unrichtig, unvollständig oder missverständlich sein. Die Kundin prüft jeden erzeugten Inhalt vor
        der Veröffentlichung auf Richtigkeit, Vollständigkeit und Rechtskonformität. Veröffentlichte
        KI-Ergebnisse gelten im Verhältnis der Parteien als Inhalte der Kundin (A 3). Eine Gewährleistung
        für KI-Ergebnisse ist ausgeschlossen.</p>
        <p><strong>Kein Modelltraining.</strong> Die übermittelten Inhalte werden nicht zum Training von
        KI-Modellen verwendet. Dies entspricht den Azure-OpenAI-Bedingungen von Microsoft.</p>
        <p><strong>Einordnung nach der KI-Verordnung.</strong> Die Verordnung (EU) 2024/1689 ist seit dem
        2. August 2026 allgemein anwendbar. Der Assistent ist weder eine nach Art. 5 verbotene Praktik noch
        ein Hochrisiko-System nach Art. 6 in Verbindung mit Anhang III. Er ist in der Oberfläche als
        KI-Funktion gekennzeichnet (Art. 50 Abs. 1). Soweit erzeugte Inhalte über eine blosse Unterstützung
        bei der Textbearbeitung hinausgehen, unterliegen sie der Kennzeichnungspflicht nach Art. 50 Abs. 2;
        die Anbieterin setzt diese Kennzeichnung um. Die Kundin darf den Assistenten nicht für nach Art. 5
        verbotene Praktiken einsetzen, insbesondere nicht zur Emotionserkennung, zur biometrischen
        Kategorisierung oder zur Bewertung von Personen, und gibt keine Personendaten als Eingabe ein.
        Nutzt die Kundin den Assistenten im eigenen Namen, treffen sie die Betreiberpflichten der
        KI-Verordnung, soweit sie in deren Anwendungsbereich fällt.</p>
      `,
        },
        {
            num: 'A 5',
            title: 'Bildschirme, Abspielgeräte und Aufstellung',
            body: `
        <p>Der Abspielclient läuft auf einem Gerät der Kundin. Sofern die Anbieterin nicht ausdrücklich
        Hardware liefert (dann gilt § 13 AGB), beschafft, betreibt und wartet die Kundin die Geräte selbst.
        Sie sorgt für geeignete Stromversorgung, Netzanbindung, Montage und physische Sicherung sowie für
        die Einhaltung der am Aufstellort geltenden bau-, arbeits- und sicherheitsrechtlichen Vorgaben.</p>
        <p>Die Kundin hält die Systemvoraussetzungen des Abspielclients ein und lässt dessen automatische
        Aktualisierung zu. Für Störungen, die auf ungeeigneter Hardware, unterbrochener Netzanbindung oder
        eigenmächtigen Änderungen am Gerät beruhen, haftet die Anbieterin nicht.</p>
        <p>Zur Störungsbehebung darf die Anbieterin einen Bildschirm aus der Ferne neu laden, neu koppeln
        oder dessen angezeigten Inhalt wechseln. Solche Eingriffe erfolgen nur, soweit für den Betrieb
        erforderlich, und werden protokolliert. Eine Beobachtung des Aufstellorts findet nicht statt.</p>
      `,
        },
        {
            num: 'A 6',
            title: 'QR-Code-Statistik',
            body: `
        <p>Blendet die Kundin auf einer Inhaltsfolie einen QR-Code ein, erfasst ScreenDeck beim Scannen ein
        Ereignis mit Geräte- und Browserangaben des scannenden Endgeräts und stellt der Kundin daraus eine
        Reichweitenstatistik bereit. Einzelheiten regelt Ziff. 08 der Datenschutzerklärung ScreenDeck.</p>
        <p>Verantwortliche Stelle für diese Bearbeitung ist die Kundin; die Anbieterin handelt als
        Auftragsbearbeiterin (§ 11 AGB). Die Kundin ist verpflichtet, am Aufstellort in geeigneter Form auf
        die Statistik hinzuweisen und, soweit das an ihrem Standort geltende Recht dies verlangt, eine
        Einwilligung einzuholen. Auf Wunsch der Kundin deaktiviert die Anbieterin die Funktion für den
        gesamten Mandanten.</p>
      `,
        },
        {
            num: 'A 7',
            title: 'Verfügbarkeit und Support',
            body: `
        <p>Es gelten die Verfügbarkeits- und Supportregelungen nach § 10 AGB einschliesslich des dort für
        ScreenDeck genannten Zielwerts. Diese Produktbedingungen legen keine abweichenden Werte fest.</p>
        <p>Der Abspielclient hält zuletzt ausgelieferte Inhalte lokal vor und zeigt sie bei einer Störung
        der Verbindung weiter an. Eine Unterbrechung der Verbindung führt daher in der Regel nicht sofort
        zu einem leeren Bildschirm; eine Gewähr dafür besteht nicht.</p>
      `,
        },
        {
            num: 'A 8',
            title: 'Vergütung ScreenDeck',
            body: `
        <p>Es gilt § 8 AGB. Die Vergütung für ScreenDeck richtet sich nach der jeweils vereinbarten Offerte
        oder Bestellung. Soweit im Einzelfall keine Vergütung vereinbart ist, wird ScreenDeck unentgeltlich
        bereitgestellt; die Anbieterin kann eine unentgeltliche Bereitstellung mit einer Frist von
        <strong>60 Tagen</strong> in Textform beenden oder auf ein entgeltliches Modell umstellen. Die
        Kundin kann den Vertrag in diesem Fall auf den Zeitpunkt des Wirksamwerdens kündigen.</p>
        <p>Geräte, Lizenzen Dritter, Datenmigrationen und Einsätze vor Ort werden nach § 8 AGB gesondert
        verrechnet.</p>
      `,
        },
        {
            num: 'A 9',
            title: 'Daten bei Vertragsende',
            body: `
        <p>Es gilt § 12 AGB. Anzeigeinhalte, Wiedergabelisten und Bildschirmkonfigurationen kann die Kundin
        jederzeit über die Plattform exportieren. Nach Vertragsende werden sie nach Ablauf der Übergangsfrist
        gemäss § 12 AGB gelöscht. Es obliegt der Kundin, benötigte Inhalte rechtzeitig zu sichern.</p>
      `,
        },
    ],
};

const EN: LegalDoc = {
    title: 'ScreenDeck Product Terms',
    meta: 'Annex to the General Terms and Conditions · As at 4 August 2026',
    tocHeading: 'Contents',
    footer: 'The German version prevails. Translations are provided for convenience.',
    sections: [
        {
            num: 'A 1',
            title: 'Subject matter and order of precedence',
            body: `
        <p>These product terms supplement the General Terms and Conditions of Gastro Management Group
        Oberbillig (the "GTC") for the <strong>ScreenDeck</strong> product. They govern only what applies
        specifically to ScreenDeck.</p>
        <p><strong>Order of precedence:</strong> the GTC otherwise apply unchanged, in particular
        remuneration (§ 8 GTC), term and termination (§ 9 GTC), availability (§ 10 GTC), warranty
        (§ 14 GTC), liability (§ 15 GTC) and governing law and jurisdiction (§ 23 GTC). Where these product
        terms and the GTC conflict, these product terms prevail for ScreenDeck. An individual written
        agreement prevails over both.</p>
        <p>These product terms are addressed exclusively to businesses and commercially acting
        organisations. No contract is formed with consumers.</p>
      `,
        },
        {
            num: 'A 2',
            title: 'Scope of the ScreenDeck service',
            body: `
        <p>ScreenDeck is a web-based digital signage platform. Depending on the functions enabled for the
        customer, the service comprises:</p>
        <ul>
          <li>management of screens, playlists and slides, including a change record</li>
          <li>multi-tenant user and role management</li>
          <li>real-time control of connected screens</li>
          <li>schedule-based playback by weekday and time slot</li>
          <li>automatic preview images of slides</li>
          <li>QR code scan statistics (A 6)</li>
          <li>maintenance banner and automatic updating of the player client</li>
          <li>integrated support chat with email notification</li>
          <li>optional AI assistant for content creation (A 4)</li>
        </ul>
        <p>The applicable feature set follows from the product description in force. § 2 GTC on further
        development and on notice of material restrictions applies accordingly.</p>
      `,
        },
        {
            num: 'A 3',
            title: 'Responsibility for display content',
            body: `
        <p>The customer is solely responsible for all content it publishes on its screens through
        ScreenDeck. It warrants that such content:</p>
        <ul>
          <li>infringes no copyright, trade mark or other third-party rights,</li>
          <li>does not breach applicable law, in particular the Unfair Competition Act, the Criminal Code
              and data protection law,</li>
          <li>contains no misleading, defamatory, discriminatory, pornographic or otherwise offensive
              material,</li>
          <li>complies with food law requirements for prices, allergens and origin; the information given
              on site always prevails.</li>
        </ul>
        <p>Where personal data is displayed — for instance names or photographs of staff — the customer
        ensures that it is entitled to do so and has informed the people concerned. The indemnity under
        § 16 GTC applies to such content without restriction.</p>
        <p>The customer designates at least one administering person per tenant. That person is responsible
        for managing sub-accounts and for deactivating the accounts of departing staff without delay.</p>
      `,
        },
        {
            num: 'A 4',
            title: 'AI assistant',
            body: `
        <p>ScreenDeck optionally provides an AI assistant for content creation. It is based on Microsoft's
        <strong>Azure OpenAI Service</strong> and operated in European data centres. § 7 GTC (AI functions)
        applies in full; in addition:</p>
        <p><strong>Quotas and limits.</strong> Use is capped by monthly quotas per tenant and per user.
        Administering persons may set tenant-specific limits; otherwise the provider's default values
        apply. Once a quota is exhausted, further requests are declined with a notice. Unused or already
        consumed quotas are not refunded. The provider may adjust quotas, limits and the model used;
        material lasting restrictions are announced at least 14 days in advance.</p>
        <p><strong>The customer's duty to review.</strong> AI results are non-binding suggestions and may be
        inaccurate, incomplete or misleading. The customer reviews every generated item for accuracy,
        completeness and legal compliance before publication. Published AI results count as the customer's
        content as between the parties (A 3). Any warranty for AI results is excluded.</p>
        <p><strong>No model training.</strong> Transmitted content is not used to train AI models, in
        accordance with Microsoft's Azure OpenAI terms.</p>
        <p><strong>Classification under the AI Act.</strong> Regulation (EU) 2024/1689 has been generally
        applicable since 2 August 2026. The assistant is neither a practice prohibited under Art. 5 nor a
        high-risk system under Art. 6 in conjunction with Annex III. It is labelled as an AI function in the
        interface (Art. 50(1)). Where generated content goes beyond mere assistance with standard editing,
        it is subject to the marking obligation under Art. 50(2), which the provider is implementing. The
        customer must not use the assistant for practices prohibited under Art. 5, in particular not for
        emotion recognition, biometric categorisation or the assessment of individuals, and must not enter
        personal data as input. Where the customer uses the assistant in its own name, the deployer
        obligations of the AI Act apply to it in so far as it falls within the Regulation's scope.</p>
      `,
        },
        {
            num: 'A 5',
            title: 'Screens, player devices and installation',
            body: `
        <p>The player client runs on a device of the customer. Unless the provider expressly supplies
        hardware (in which case § 13 GTC applies), the customer procures, operates and maintains the devices
        itself. It provides suitable power supply, network connectivity, mounting and physical security and
        complies with the building, employment and safety requirements applicable at the installation
        site.</p>
        <p>The customer meets the system requirements of the player client and permits its automatic
        updating. The provider is not liable for faults caused by unsuitable hardware, interrupted network
        connectivity or unilateral changes to the device.</p>
        <p>To remedy faults, the provider may remotely reload, re-pair or switch the content shown on a
        screen. Such actions are taken only in so far as necessary for operation and are logged. There is
        no observation of the installation site.</p>
      `,
        },
        {
            num: 'A 6',
            title: 'QR code statistics',
            body: `
        <p>Where the customer shows a QR code on a slide, ScreenDeck records an event on scanning, including
        device and browser details of the scanning device, and provides the customer with reach statistics
        derived from it. Section 08 of the ScreenDeck privacy policy sets out the details.</p>
        <p>The customer is the controller for this processing; the provider acts as processor (§ 11 GTC).
        The customer must give suitable notice of the statistics at the installation site and, where the law
        applicable at its location so requires, obtain consent. At the customer's request the provider
        disables the function for the entire tenant.</p>
      `,
        },
        {
            num: 'A 7',
            title: 'Availability and support',
            body: `
        <p>The availability and support provisions of § 10 GTC apply, including the target value stated
        there for ScreenDeck. These product terms set no diverging values.</p>
        <p>The player client caches the content last delivered and continues to display it if the connection
        fails. An interruption therefore does not usually result in an immediately blank screen, though this
        is not warranted.</p>
      `,
        },
        {
            num: 'A 8',
            title: 'ScreenDeck remuneration',
            body: `
        <p>§ 8 GTC applies. Remuneration for ScreenDeck follows the quotation or order agreed in each case.
        Where no remuneration is agreed in a particular case, ScreenDeck is provided free of charge; the
        provider may end free provision or move to a paid model on <strong>60 days'</strong> notice in text
        form. The customer may in that case terminate the contract with effect from the date the change
        takes effect.</p>
        <p>Devices, third-party licences, data migrations and on-site work are charged separately under
        § 8 GTC.</p>
      `,
        },
        {
            num: 'A 9',
            title: 'Data on termination',
            body: `
        <p>§ 12 GTC applies. The customer can export display content, playlists and screen configurations
        from the platform at any time. After the contract ends they are deleted once the transition period
        under § 12 GTC has expired. It is for the customer to secure any content it needs in good time.</p>
      `,
        },
    ],
};

export const SCREENDECK_TERMS: LegalDocSet = { de: DE, en: EN };
